/**
 * El mismo dato con distinto nombre según el país.
 *
 * Business Central no publica un campo con un solo nombre: cada localizado le
 * pone el suyo. La hora de creación de un documento registrado se llama
 * `Hora_creacion` en Ecuador y `EXCRICreationTime` en Costa Rica; el cajero,
 * `ID_Cajero` y `EXCRICashierId`; el enlace del abono con su factura,
 * `No_Comprobante_Fiscal_Rel` y `EXCCRIRelatedFiscalReceiptNo`.
 *
 * El pull preguntaba sólo por el nombre de Ecuador. Como el JSON no valida
 * nada, una clave que no existe no da error: **se comporta igual que un campo
 * vacío**. Así que el dato llegaba en la respuesta —el pull no usa `$select`,
 * viene entero— y el mapeo lo tiraba. Cuatro campos, cuatro veces el mismo
 * mecanismo, y cada uno se descubrió por separado.
 *
 * ## Por qué una cadena de candidatos y no un `if (país)`
 *
 * Business Central va a publicar los nombres neutros —`HoraCreacionTPV`,
 * `TurnoTPV`, `IdCajeroTPV`, `CampanaTPV`— en la extensión 1.0.4.84
 * (`contrato-correccion-ciclo-costa-rica.md`, ronda 3). Mientras tanto conviven
 * el nombre viejo y el del localizado, y los ambientes **no se actualizan a la
 * vez**: hoy mismo, Costa Rica TEST está tres binarios por detrás de DEV.
 *
 * Con una cadena de candidatos el terminal funciona con los tres nombres y no
 * depende del orden de despliegue. Con un `if (país)` habría que volver a tocar
 * el mapeo el día que salga cada versión, y adivinar qué ambiente tiene cuál.
 */

/**
 * El primer candidato que traiga algo.
 *
 * Salta los vacíos, y eso es lo que hace que sirva: en Costa Rica `ID_Cajero`
 * **está publicado y llega vacío** mientras el valor viaja en
 * `EXCRICashierId`. Sin saltar los vacíos, el primero ganaría y el dato se
 * seguiría perdiendo. Business Central devuelve además `" "` —un espacio— para
 * una opción sin valor, así que tampoco cuenta como valor.
 *
 * @param {object} apiObj  El documento tal como lo devuelve Business Central.
 * @param {string[]} claves  Los nombres a probar, en orden de preferencia.
 * @returns {*} El valor encontrado, o `undefined` si ninguno trae nada.
 */
export const primerValor = (apiObj, claves) => {
  for (const clave of claves) {
    const valor = apiObj?.[clave];
    if (valor === null || valor === undefined) continue;
    if (typeof valor === "string" && valor.trim() === "") continue;
    return valor;
  }
  return undefined;
};

/** Orden: el nombre de siempre, el neutro acordado, y el del localizado de Costa Rica. */
export const CLAVES = {
  /**
   * El tipo de documento, que no se llama igual en las dos entidades.
   *
   * `SalesInvoice` lo publica como `Document_Type` con valor «Invoice».
   * `SalesCreditMemo` **no publica ese nombre**: lo publica como
   * `TipoDocumento`, con valor «Credit Memo». Medido el 16/10/2026 en las tres
   * empresas que comparten el ambiente --Costa Rica, Panamá y Puerto Rico--.
   *
   * Sin esta cadena, una nota de crédito traída de históricos se guardaba con el
   * tipo en blanco, y el blanco no casa ni con «Credit Memo» ni con «Invoice»:
   * la fila quedaba en la base local sin salir en ninguna de las dos listas,
   * después de un mensaje que decía que la sincronización fue correcta.
   *
   * No es de un país. Lo que lo tapa en Costa Rica y Panamá es que allá la nota
   * suele venir ya contabilizada, y ese camino usa `mapPostedInvoice`, que
   * recibe el tipo por parámetro. Basta una nota sin registrar para reproducirlo.
   */
  tipoDeDocumento: ["Document_Type", "TipoDocumento"],
  hora: ["Hora_creacion", "HoraCreacionTPV", "EXCRICreationTime"],
  cajero: ["ID_Cajero", "IdCajeroTPV", "EXCRICashierId"],
  turno: ["Turno", "TurnoTPV"],
  campana: ["Campaign_No_", "Campaign_No", "CampanaTPV"],
  /**
   * El comprobante que corrige un abono.
   *
   * **El orden importa y estaba mal.** `primerValor` devuelve el primero no
   * vacío, así que un campo que siempre viene relleno con algo inútil bloquea a
   * todos los que van detrás. Eso pasaba con `No_Comprobante_Fiscal_Rel`:
   * medido en Ecuador el 04/09/2026, está informado en **1.264 de 1.264** y lo
   * que lleva es el **consecutivo fiscal** --`"000000001"`, sólo dígitos--,
   * nunca un número de documento. Iba primero, así que el enlace del abono con
   * su factura no se reconstruía nunca aunque el dato estuviera al lado.
   *
   * Los dos que sí llevan un documento, medidos:
   *
   * - **Ecuador**: `Anula_a_Documento`, informado en **852 de 1.264**, con
   *   valores como `"PVMDSC1R-000000001"` y `"VFR-001858"`.
   * - **Costa Rica**: `EXCCRIHistoricalDocumentNo`, informado en **2.505 de
   *   2.656**, con valores como `"VFR15-000100"`; las cinco de la muestra
   *   cruzan con una factura registrada que existe. El
   *   `EXCCRIRelatedFiscalReceiptNo` que se usaba está informado en menos
   *   (1.219) y `No_Comprobante_Fiscal_Rel` **ni siquiera existe** en esa
   *   entidad.
   *
   * Los dos fiscales se quedan al final: no hacen daño donde no hay otra cosa,
   * y alguna vez podrían traer un `No` de verdad. Pero ya no tapan a los buenos.
   *
   * Importa porque sin enlace `getRemainingQuantitiesForInvoice` no puede
   * descontar lo ya devuelto, y el terminal deja devolver dos veces lo mismo.
   * Acuerdo `contrato-nota-de-credito-sobre-historicos`, ronda 4.
   */
  comprobanteRelacionado: [
    "Anula_a_Documento",
    "EXCCRIHistoricalDocumentNo",
    "EXCPANHistoricalDocumentNo",
    "No_Comprobante_Fiscal_Rel",
    "EXCCRIRelatedFiscalReceiptNo",
  ],
};

/**
 * Qué clase de comprobante corrige un abono, según Business Central.
 *
 * Es el campo «Tipo Doc. Ref NC» —`EXCCRIReferencedDocumentType`—, y desde la
 * extensión 1.0.4.86 viene relleno en la nota de crédito **registrada**
 * (medido: `VNR15-000306` trae `"Tiquete Electronico"`; las dos anteriores del
 * mismo día lo traen vacío).
 *
 * Importa porque es la **única** fuente para un abono traído por Traer
 * Históricos: el creado en el terminal hereda el tipo del documento que
 * corrige, pero el traído no lo trae —`PostedSalesCreditMemo` no publica
 * `EXCCRIElectronicDocumentType`—, así que sin esto el listado no puede
 * distinguirlo.
 *
 * El vocabulario no coincide entre los dos campos: la factura dice `"Tiquete"`
 * y el abono `"Tiquete Electronico"`. Se normaliza por lo que contiene y no por
 * igualdad, que además sobrevive a que alguien le ponga la tilde.
 *
 * @returns {string|undefined} `"Tiquete"`, `"Factura"`, o nada si no se sabe.
 */
export const comprobanteCorregido = (apiObj) => {
  const crudo = String(apiObj?.EXCCRIReferencedDocumentType ?? "").trim().toLowerCase();
  if (!crudo) return undefined;
  if (crudo.includes("tiquete")) return "Tiquete";
  if (crudo.includes("factura")) return "Factura";
  return undefined;
};

/** El tipo de documento, se llame como se llame en la entidad que lo trae. */
export const tipoDeDocumentoBC = (apiObj) => primerValor(apiObj, CLAVES.tipoDeDocumento);

/** La hora del alta, en `HH:mm:ss`, mire el país que mire. */
export const horaDelDocumento = (apiObj) => primerValor(apiObj, CLAVES.hora);

/** Quién atendió la venta. */
export const cajeroDelDocumentoBC = (apiObj) => primerValor(apiObj, CLAVES.cajero);

/** El comprobante que este abono corrige, venga por donde venga. */
export const comprobanteRelacionado = (apiObj) =>
  primerValor(apiObj, CLAVES.comprobanteRelacionado);

/**
 * A qué documento se aplica un abono.
 *
 * `Applies_to_Doc_No` es lo que el terminal manda y lo que debería volver, pero
 * la importación de Costa Rica no lo escribe todavía —lo hará en 1.0.4.84, y
 * sobre la nota de crédito **registrada**, no sobre el borrador—. Mientras
 * tanto el dato sí está, en el campo del comprobante relacionado.
 *
 * Sin este respaldo, un abono traído por Traer Históricos vuelve sin enlace,
 * `getCreditMemosForInvoice` no encuentra las devoluciones previas de esa
 * factura y **la guarda contra devolver dos veces falla abierta**.
 *
 * El tipo no tiene respaldo posible: el campo relacionado guarda un número y no
 * dice de qué. Se asume `Invoice`, que es lo único que un abono corrige en este
 * flujo --y lo único que Business Central admite en esa clave, también para la
 * boleta de Perú.
 *
 * @returns {{No: string, Type: string}} Vacíos si no hay enlace por ningún lado.
 */
export const documentoAlQueSeAplica = (apiObj, { conRespaldo = true } = {}) => {
  const directo = primerValor(apiObj, ["Applies_to_Doc_No"]);
  if (directo) {
    return {
      No: String(directo),
      Type: primerValor(apiObj, ["Applies_to_Doc_Type"]) ?? "Invoice",
    };
  }

  // El respaldo sólo vale para un abono. Sus campos --el histórico de Costa
  // Rica, el `Anula_a_Documento` de Ecuador-- vienen rellenos también en una
  // factura, pero ahí significan otra cosa: **su propio** número en el sistema
  // anterior, no una referencia a otro documento. Deducir de ellos dejaba a cada
  // factura traída apuntándose a sí misma.
  //
  // El campo directo, en cambio, se respeta siempre: si Business Central dice
  // explícitamente a qué se aplica un documento, eso es una afirmación suya y no
  // la contradice una deducción nuestra.
  if (!conRespaldo) return { No: "", Type: "" };

  const relacionado = comprobanteRelacionado(apiObj);
  if (relacionado) return { No: String(relacionado), Type: "Invoice" };

  return { No: "", Type: "" };
};
